import {
  AppstoreOutlined,
  CrownOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Alert, Button, Input, Modal, Space, Tag, theme } from 'antd';
import NameFilter from 'components/filters/NameFilter';
import AdministradorForm from 'components/forms/Administradores/AdministradorForm';
import AdministradoresList from 'components/lists/Administradores/AdministradoresList';
import { CardHeader, PageHeader } from 'components/headings';
import useTenantFlags from 'hooks/useTenantFlags';
import AdministradorTabsConfig from 'pages/admin/AdministradorTabsConfig/AdministradorTabsConfig';
import { useAdministradoresProvider } from 'providers/Administradores/Administradores';
import React, { useEffect, useState } from 'react';

const { useToken } = theme;

const Administradores = () => {
  const { token } = useToken();
  const { readOnly } = useTenantFlags();
  const [open, setOpen] = useState(false);
  const [tabsOpen, setTabsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { call, hasCalled, addFilter, data, error } = useAdministradoresProvider();

  useEffect(() => {
    if (!hasCalled) call();
  }, [call, hasCalled]);

  const handleOpen = (_open) => setOpen(_open ?? !open);

  const refresh = () => {
    handleOpen(false);
    call();
  };

  const nameFilter = (value) => {
    addFilter({ fullname: value === true ? null : value });
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    addFilter({ fullname: value || null });
  };

  const panelStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    overflow: 'hidden',
    background: token.colorBgContainer,
    boxShadow: token.boxShadowSecondary,
  };

  if (error) {
    return (
      <div style={{ paddingTop: token.paddingLG }}>
        <Alert
          message="Error al cargar administradores"
          description="No se pudieron cargar los datos de administradores."
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => call()}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={CrownOutlined}
        title="Administradores de Edificios"
        subtitle="Gestiona los administradores de edificios y sus clientes"
        actions={
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar por nombre…"
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              Actualizar
            </Button>
            {!readOnly && (
              <Button icon={<AppstoreOutlined />} onClick={() => setTabsOpen(true)}>
                Configurar tabs
              </Button>
            )}
            {!readOnly && (
              <Button type="primary" icon={<UserAddOutlined />} onClick={handleOpen}>
                Agregar administrador
              </Button>
            )}
          </Space>
        }
      />

      <div
        style={{
          display: 'grid',
          gap: token.marginMD,
          gridTemplateColumns: 'minmax(0, 17fr) minmax(0, 7fr)',
        }}
        className="admin-layout"
      >
        {/* Lista de administradores */}
        <div style={panelStyle}>
          <CardHeader
            icon={TeamOutlined}
            tint={token.colorSuccess}
            title="Lista de administradores"
            subtitle="Todos los administradores del tenant y sus clientes asignados"
            action={
              data ? (
                <Tag color="default" style={{ margin: 0, fontWeight: 600 }}>
                  {data.length} {data.length === 1 ? 'administrador' : 'administradores'}
                </Tag>
              ) : null
            }
          />
          <div style={{ padding: token.paddingSM }}>
            <AdministradoresList refresh={refresh} />
          </div>
        </div>

        {/* Filtros */}
        <div style={panelStyle}>
          <CardHeader
            icon={FilterOutlined}
            tint={token.colorInfo}
            title="Filtros"
            subtitle="Afina la lista por atributos del administrador"
          />
          <div
            style={{ padding: token.paddingMD, display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <NameFilter onChange={(value) => nameFilter(value)} />
          </div>
        </div>
      </div>

      {/* Modal de creación */}
      <Modal
        open={open}
        onCancel={() => handleOpen(false)}
        footer={false}
        title={
          <Space>
            <UserAddOutlined style={{ color: token.colorPrimary }} />
            <span>Agregar nuevo administrador</span>
          </Space>
        }
        width={700}
        destroyOnHidden
      >
        <AdministradorForm refresh={refresh} />
      </Modal>

      <AdministradorTabsConfig open={tabsOpen} onClose={() => setTabsOpen(false)} />

      <style>{`
        @media (max-width: 1024px) {
          .admin-layout { grid-template-columns: minmax(0, 1fr) !important; }
        }
      `}</style>
    </div>
  );
};

export default Administradores;
